import React, { useCallback, useEffect, useState } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon  from '@material-ui/icons/Delete';
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CheckCircleIcon  from '@material-ui/icons/CheckCircle';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';

// Components
import { IFormOption } from '../../_core/_ui/forms.component';
import { Spacer } from '../../_core/_ui/structure.components';
import { TextField, Tooltip } from '@material-ui/core';
import { GroupStageBuilderContainer } from '../GroupStageBuilder/group-stage-builder.container';
import { SessionStageBuilderContainer } from '../SessionStageBuilder/session-stage-builder.container';
import { StageItemPre, StageItemTitle } from '../assessment-stage.shared-components';
import { AddBox } from '@material-ui/icons';
import { RouteStageBuilderContainer } from '../RouteStageBuilder/route-stage-builder.container';
import { StageSubmitRegistrar } from '../../_core/Formik/formik-submit-registrar.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { getStageTypeLabel } from '../../../lib/helpers/assessment-stage.helpers';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from './assessment-stage-builder.messages';

interface IAssessmentStageBuilderItem {
  sessionNum: number;
  stage: IAssessmentStage;
  stages: IAssessmentStage[];
  index: number;
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  ageGroup?: IAgeGroup | null;
  positionOptions: IFormOption[];
  completeStage: Function;
  createStage: Function;
  updateStage: Function;
  removeStage: Function;
  moveStage?: (stage: IAssessmentStage, direction: 'UP' | 'DOWN') => Promise<void>;
  isChangingStructure?: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  isSelectionDisabled: boolean;
  onToggleSelect: (index: number) => void;
  previewMode?: boolean;
  // Config-editable template stage: inputs live, but no move/remove/complete structural controls.
  templateEditMode?: boolean;
  // Editing a CATALOG definition's settings: titles and per-band derived fields are seed-owned,
  // so their inputs hide; only settable config keys stay live.
  catalogSettingsMode?: boolean;
  // Lets a parent-level Save flush a stage form whose own dirty-gated button is out of view.
  registerStageSubmit?: StageSubmitRegistrar;
}

const STAGE_COMPONENTS: { [key: string]: React.FC<any> } = {
  GROUP: GroupStageBuilderContainer,
  ROUTE: RouteStageBuilderContainer,
  SESSION: SessionStageBuilderContainer
}

export const AssessmentStageBuilderItem: React.FC<IAssessmentStageBuilderItem> = ({
  sessionNum,
  stage,
  stages,
  index,
  completeStage,
  createStage,
  updateStage,
  removeStage,
  moveStage,
  isChangingStructure,
  ageGroup,
  selectionMode,
  isSelected,
  isSelectionDisabled,
  onToggleSelect,
  previewMode,
  templateEditMode,
  catalogSettingsMode,
  registerStageSubmit,
  ...rest
}) => {
  const termLookup = useCompanySportTerminology();

  // Preview and template-edit show every stage expanded; otherwise default to collapsed when completed.
  const [isCollapsed, setCollapsed] = useState(previewMode || templateEditMode ? false : stage.isCompleted);
  const StageComponent = STAGE_COMPONENTS[stage.type];

  const [titleDraft, setTitleDraft] = useState(stage.title ?? '');
  // Resync the draft when the stored title changes underneath us (e.g. a server refetch
  // after an optimistic write) so the input never keeps a stale value.
  useEffect(() => { setTitleDraft(stage.title ?? ''); }, [stage.title]);
  // Title editing shares the same gate as the move/remove controls — not while
  // previewing a template, selecting stages, or once the stage is completed.
  const canEditTitle = !previewMode && !selectionMode && !stage.isCompleted && !catalogSettingsMode;
  const commitTitle = useCallback(() => {
    const trimmed = titleDraft.trim();
    if (trimmed !== (stage.title ?? '')) updateStage(stage._id, { title: trimmed });
  }, [titleDraft, stage.title, stage._id, updateStage]);

  // Dim completed stages, except during template selection or preview so they read normally.
  const additionalStyle = !previewMode && stage.isCompleted && !selectionMode ? {opacity: "60%"} : undefined

  // The overlap setting only means something for a session stage that follows another one.
  // Not memoized: two cheap index reads, not fed to any hook dep array.
  const followsSessionStage = stage.type === 'SESSION' && index > 0 && stages[index - 1]?.type === 'SESSION';

  const isActiveStage = ageGroup?.currentStageNum && stage.order === ageGroup.currentStageNum
  const stagePrefix = previewMode ? "" : stage.isInProgress ? "IN PROGRESS - " : stage.isCompleted ? "COMPLETED - " : isActiveStage ? "ACTIVE - " : ""
  return (
    <Box style={{ padding: '8px', background: '#fff', borderRadius: '8px', marginTop: '16px' }}>
      <StageItemPre dark={stage.type === 'SESSION'} style={additionalStyle}>
        <IconButton onClick={() => setCollapsed(!isCollapsed)}>
          {isCollapsed ? <AddBox /> : <IndeterminateCheckBoxIcon />}
        </IconButton>
        <StageItemTitle>{stagePrefix + getStageTypeLabel(stage.type, termLookup)}{stage.type === 'SESSION' && ` #${sessionNum}`}{stage.title ? ` - ${stage.title}` : ''}</StageItemTitle>
        <Spacer />
        {selectionMode ? (
          <Tooltip title={isSelectionDisabled ? <IntlMsg {...messages.selectionDisabledTooltip} /> : ''}>
            {/* span keeps the tooltip working while the checkbox is disabled */}
            <span>
              <Checkbox
                color="primary"
                checked={isSelected}
                disabled={isSelectionDisabled}
                onChange={() => onToggleSelect(index)}
              />
            </span>
          </Tooltip>
        ) : templateEditMode ? null : (
          /* Hide edit stage options when stage is completed */
          !stage.isCompleted &&
          <>
            <Tooltip title="Move Up" disableHoverListener={stage.order <= 1}>
              <IconButton
                // Prevent moving stages to before any completed stages
                disabled={isChangingStructure || !moveStage || index === 0 || !!stages[index - 1]?.isCompleted || !!stages[index - 1]?.isInProgress || !!stage.isInProgress}
                onClick={() => moveStage?.(stage, 'UP')}
              ><ArrowUpIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Move Down" disableHoverListener={stage.order >= stages.length}>
              <IconButton
                disabled={isChangingStructure || !moveStage || index === stages.length - 1 || !!stages[index + 1]?.isCompleted || !!stages[index + 1]?.isInProgress || !!stage.isInProgress}
                onClick={() => moveStage?.(stage, 'DOWN')}
              ><ArrowDownIcon /></IconButton>
            </Tooltip>
            {!!isActiveStage &&
              <Tooltip title="Complete Stage">
                <IconButton disabled={isChangingStructure} onClick={() => completeStage(stage)}>
                  <CheckCircleIcon />
                </IconButton>
              </Tooltip>
            }
            <Tooltip title="Remove Stage">
              <IconButton disabled={isChangingStructure || !!stage.isInProgress} onClick={() => removeStage(stage._id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </StageItemPre>
      {!isCollapsed && (
        <>
          {canEditTitle && (
            <Box px={1} pt={1}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={<IntlMsg {...messages.stageTitleLabel} />}
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
              />
            </Box>
          )}
          <StageComponent
            stage={stage}
            updateStage={updateStage}
            style={additionalStyle}
            catalogSettingsMode={catalogSettingsMode}
            followsSessionStage={followsSessionStage}
            registerStageSubmit={registerStageSubmit}
            {...rest}
          />
        </>
      )}
    </Box>
  );
};
